import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'

const API = import.meta.env.VITE_API_URL ?? ''

// Module-level subscriber set. Each mounted useUnreviewedAlertCount instance
// registers a trigger; invalidateAlertCount fires all of them so every
// subscriber refetches without knowing about the others.
const subscribers = new Set<() => void>()

// Debounce timer: bulk-review calls markReviewed() once per alert. We coalesce
// all invalidations within a 400ms window into a single refetch so the badge
// doesn't issue one count request per alert in a bulk operation.
let debounceTimer: ReturnType<typeof setTimeout> | null = null
const DEBOUNCE_MS = 400

/** Call after any mutation that changes the org's unreviewed alert count.
 *  Multiple calls within 400 ms are coalesced into one refetch. */
export function invalidateAlertCount(): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    for (const fn of subscribers) fn()
  }, DEBOUNCE_MS)
}

/** Returns the exact server-side count of fresh, decision-worthy Today alerts.
 *  Refreshes on mount, on invalidation, and when the app regains visibility.
 *  Only one instance should be mounted (Layout). */
export function useUnreviewedAlertCount(): number {
  const { getToken } = useAuth()
  const [count, setCount] = useState(0)
  // Incrementing this triggers the fetch effect.
  const [tick, setTick] = useState(0)

  // Register with the module-level subscriber set.
  useEffect(() => {
    const trigger = () => setTick((n) => n + 1)
    subscribers.add(trigger)
    return () => { subscribers.delete(trigger) }
  }, [])

  // Fetch the exact pre-LIMIT count whenever tick changes.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getToken()
        if (cancelled) return
        // limit=0 returns zero rows but the fixed endpoint still runs the
        // count query independently, so data.total is accurate.
        const res = await fetch(`${API}/api/v1/alerts?reviewed=false&surface=today&limit=0`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok || cancelled) return
        const data = await res.json()
        if (!cancelled) setCount(data?.data?.total ?? 0)
      } catch { /* badge stays at last known value on transient errors */ }
    })()
    return () => { cancelled = true }
  }, [getToken, tick])

  // Refetch when the tab regains visibility (e.g. switching back from another
  // browser tab or unlocking the screen). Debounced 1s to avoid a burst.
  useEffect(() => {
    let debounce: ReturnType<typeof setTimeout> | null = null
    const onVisible = () => {
      if (document.hidden) return
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(() => setTick((n) => n + 1), 1000)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      if (debounce) clearTimeout(debounce)
    }
  }, [])

  return count
}
